/**
 * Generic undo/redo manager.
 *
 * Works with any serialisable state — the caller provides two functions:
 *   serialize()          → string   (snapshot the current state)
 *   restore(snapshot)    → void | Promise<void>  (apply a previous snapshot)
 *
 * Pattern: mutate state, then call push() to snapshot the result.
 *
 * Usage:
 *   const undo = new UndoManager({
 *     serialize: () => JSON.stringify(state),
 *     restore:   (s) => { state = JSON.parse(s); },
 *   });
 *   undo.initialize();          // capture initial state
 *   // ... mutate state ...
 *   undo.push();                // after each mutation
 *   await undo.undo();
 *   await undo.redo();
 */
export class UndoManager {
  /**
   * @param {Object} opts
   * @param {() => string} opts.serialize - Returns a JSON string of the current state
   * @param {(snapshot: string) => void | Promise<void>} opts.restore - Applies a snapshot
   * @param {number} [opts.maxSize=50] - Maximum number of history entries
   */
  constructor({ serialize, restore, maxSize = 50 }) {
    this._serialize = serialize;
    this._restore = restore;
    this._maxSize = maxSize;
    this._stack = [];
    this._index = -1;
    this._restoring = false;
    this.onStateChange = null;
  }

  get canUndo() { return this._index > 0; }
  get canRedo() { return this._index < this._stack.length - 1; }
  get state() { return { canUndo: this.canUndo, canRedo: this.canRedo }; }

  /** Capture initial state. Call once after setup. */
  initialize() {
    this._stack = [];
    this._index = -1;
    this.push();
  }

  /** Snapshot current state. Call after each mutation. */
  push() {
    if (this._restoring) return;

    const snapshot = this._serialize();

    // Deduplicate consecutive identical snapshots
    if (this._stack[this._index] === snapshot) return;

    // Discard redo history when a new action is performed
    if (this._index < this._stack.length - 1) {
      this._stack = this._stack.slice(0, this._index + 1);
    }

    this._stack.push(snapshot);

    if (this._stack.length > this._maxSize) {
      this._stack.shift();
    } else {
      this._index++;
    }

    this._notify();
  }

  /** @returns {Promise<boolean>} true if undo succeeded */
  async undo() {
    if (!this.canUndo) return false;
    this._index--;
    await this._apply();
    this._notify();
    return true;
  }

  /** @returns {Promise<boolean>} true if redo succeeded */
  async redo() {
    if (!this.canRedo) return false;
    this._index++;
    await this._apply();
    this._notify();
    return true;
  }

  /** @private */
  async _apply() {
    const snapshot = this._stack[this._index];
    if (snapshot == null) return;
    this._restoring = true;
    try {
      await this._restore(snapshot);
    } finally {
      this._restoring = false;
    }
  }

  /** @private */
  _notify() {
    this.onStateChange?.(this.state);
  }
}
